import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { SCROLL_OFFSET } from 'components/WorkspaceEditor/features/scrollToNewContent/constants'
import {
    findContentElement,
    isElementVisible,
} from 'components/WorkspaceEditor/features/scrollToNewContent/utils'
import { useCallback, useEffect } from 'react'
import { useWorkspace2Store } from '../../../Workspace2Store'
import { useNotification } from '../../../services'
import { ContentItemViewNodesType } from '../../../types'
import type { DataNode } from '../../../types/types'
import {
    INCONSISTENT_PARAGRAPH_LEVEL_ERROR,
    NO_VALID_DEMOTE_PARENT_ERROR,
    NOT_FOUND,
    PromoteDemoteActions,
} from '../constants'
import {
    beginLocalPromoteDemote,
    pendingPromoteDemoteMove,
} from '../services/localPromoteDemote'
import { usePromoteDemoteStore } from '../store/PromoteDemoteStore'
import { PromoteDemoteAction } from '../types'
import {
    createOptimisticTreeState,
    findNodeById,
    wouldExceedDepthLimit,
} from '../utils'

type RegisterContentItem = (
    contentItem: Partial<TextContentElement>,
    isInProject: boolean,
    contentItemViewNodes: Partial<ContentItemViewNodesType>[]
) => void

/**
 * Re-parents a node in the tree client-side, with no server round trip of its own.
 * Queues a pending move for the caller to commit, unless it's the unsaved temp node.
 */
export const useLocalPromoteDemote = (
    activeItem: Partial<TextContentElement> | null,
    isTempNodeActive: boolean,
    contentItemViewNodes: Partial<ContentItemViewNodesType>[] | undefined,
    treeData: DataNode[],
    registerContentItem: RegisterContentItem
) => {
    const notification = useNotification()

    const handleLocalPromoteDemote = useCallback(
        (action: PromoteDemoteAction) => {
            const activeItemId = activeItem?.id
            if (!activeItemId) return

            // Read live, not from props — key auto-repeat can fire this twice fast,
            // and a stale closure would re-parent against pre-move nodes.
            const {
                contentItemViewNodes: storeNodes,
                treeData: currentTreeData,
                expandedKeys,
                treeRef,
                actions: { setExpandedKeys, setContentItemViewNodesSilently },
            } = useWorkspace2Store.getState()
            const nodes = storeNodes ?? []

            if (
                wouldExceedDepthLimit(
                    activeItemId,
                    action,
                    currentTreeData,
                    5,
                    nodes
                )
            )
                return notification.error(INCONSISTENT_PARAGRAPH_LEVEL_ERROR)

            const treeAfterMove = createOptimisticTreeState(
                nodes,
                activeItemId,
                action,
                usePromoteDemoteStore.getState().paragraphLevel
            )
            const nodeNotFound = treeAfterMove === nodes
            if (nodeNotFound)
                return notification.error({ message: `${NOT_FOUND} ${action}` })

            const movedNode = findNodeById(treeAfterMove, activeItemId)
            if (!movedNode?.id) return

            const demotedWithNoParentToLandIn =
                action === PromoteDemoteActions.Demote &&
                !movedNode.dtvContentItemParentId
            if (demotedWithNoParentToLandIn)
                return notification.error(NO_VALID_DEMOTE_PARENT_ERROR)

            const newParentId = movedNode.dtvContentItemParentId
            // Stamped on the node so useTreeDataMapping's fast path can read where
            // it landed. Promote lands after its old parent; demote just appends.
            movedNode._insertAfterId =
                action === PromoteDemoteActions.Promote
                    ? findNodeById(nodes, activeItemId)?.dtvContentItemParentId
                    : undefined

            beginLocalPromoteDemote(activeItemId)
            // Brief row highlight — the caret alone is easy to lose track of
            // once the move re-parents the row somewhere else in a big tree.
            usePromoteDemoteStore.getState().setRecentlyMovedNode(movedNode.id)

            // Temp node's level saves with the paragraph — nothing else to commit.
            if (!isTempNodeActive) pendingPromoteDemoteMove.queue(activeItemId)

            // Before the tree write: rc-tree only renders an expanded parent's
            // children, so the row and its editor would vanish otherwise.
            const newParentNotExpanded =
                newParentId && !expandedKeys.includes(newParentId)
            if (newParentNotExpanded)
                setExpandedKeys([...expandedKeys, newParentId])

            // Silent = no editorDeactivationSignal bump, so the editor being
            // typed into stays open. reRenderTree would close it.
            setContentItemViewNodesSilently(treeAfterMove)

            const movedElement = findContentElement(activeItemId)
            const movedRowIsOffscreen =
                !movedElement || !isElementVisible(movedElement)
            if (movedRowIsOffscreen)
                treeRef?.current?.scrollTo?.({
                    key: movedNode.id,
                    align: 'top',
                    offset: SCROLL_OFFSET,
                })
        },
        [activeItem, isTempNodeActive, notification]
    )

    // Re-registers on every tree change — this path never moves DOM focus, so the
    // usual register-on-focus never fires to pick up the item's new level.
    useEffect(() => {
        if (!activeItem) return

        registerContentItem(
            activeItem,
            usePromoteDemoteStore.getState().isInProject,
            contentItemViewNodes ?? []
        )
    }, [
        treeData,
        isTempNodeActive,
        activeItem,
        contentItemViewNodes,
        registerContentItem,
    ])

    return { handleLocalPromoteDemote }
}
